import { useEffect, useRef, useCallback } from 'react';

interface SessionTimeoutOptions {
  inactivityTimeout?: number;
  sessionTimeout?: number;
  onTimeout?: () => void;
  onWarning?: (remainingTime: number) => void;
}

const DEFAULT_INACTIVITY_TIMEOUT = 15 * 60 * 1000;
const DEFAULT_SESSION_TIMEOUT = 24 * 60 * 60 * 1000;
const WARNING_TIME = 2 * 60 * 1000;

export const useSessionTimeout = ({
  inactivityTimeout = DEFAULT_INACTIVITY_TIMEOUT,
  sessionTimeout = DEFAULT_SESSION_TIMEOUT,
  onTimeout,
  onWarning
}: SessionTimeoutOptions = {}) => {
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sessionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  const clearTimers = useCallback(() => {
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
  }, []);

  const handleTimeout = useCallback(() => {
    clearTimers();
    if (onTimeout) {
      onTimeout();
    }
  }, [clearTimers, onTimeout]);

  const resetInactivityTimer = useCallback(() => {
    lastActivityRef.current = Date.now();

    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    inactivityTimerRef.current = setTimeout(() => {
      handleTimeout();
    }, inactivityTimeout);
  }, [inactivityTimeout, handleTimeout]);

  const resetSessionTimer = useCallback(() => {
    if (sessionTimerRef.current) {
      clearTimeout(sessionTimerRef.current);
    }

    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
    }

    warningTimerRef.current = setTimeout(() => {
      if (onWarning) {
        onWarning(WARNING_TIME);
      }
    }, sessionTimeout - WARNING_TIME);

    sessionTimerRef.current = setTimeout(() => {
      handleTimeout();
    }, sessionTimeout);
  }, [sessionTimeout, handleTimeout, onWarning]);

  useEffect(() => {
    resetInactivityTimer();
    resetSessionTimer();

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

    const handleActivity = () => {
      resetInactivityTimer();
    };

    events.forEach(event => {
      window.addEventListener(event, handleActivity);
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      clearTimers();
    };
  }, [resetInactivityTimer, resetSessionTimer, clearTimers]);

  return {
    clearTimers,
    reset: () => {
      resetInactivityTimer();
      resetSessionTimer();
    }
  };
};
